import { useCallback } from 'react';

import { BRAND_VOICE_ASSET_FIELD } from 'src/data/brandVoiceAssets';
import { useDeleteCustomerPersona } from 'src/services';
import { useAddCustomerPersonaToMyAudiences } from 'src/services/useAddCustomerPersonaToMyAudiences';
import { useBrandVoicesActions, useCustomerPersonaActions, useUiActions } from 'src/store';
import { useCustomerPersonaPopup } from 'src/views/CustomerPersonaPopup';

import { useGenerationFormEvents } from '../../services';
import { useLocalActions } from '../../store';

export const useHandlers = (brandVoice, onSave, language) => {
  const openCustomerPersonaPopup = useCustomerPersonaPopup();
  const confirmDeletePersona = useDeleteCustomerPersona();
  const customerPersonaActions = useCustomerPersonaActions();
  const brandVoicesActions = useBrandVoicesActions();
  const localActions = useLocalActions();
  const { trackClickedTargetAudience } = useGenerationFormEvents();
  const addCustomerPersonaToMyAudiences = useAddCustomerPersonaToMyAudiences();
  const uiActions = useUiActions();

  const handleSelectPersona = useCallback(
    async (persona) => {
      await localActions.selectProjectPersona(persona);
    },
    [localActions]
  );

  const handleOpenManagePersonasPopup = useCallback(async () => {
    trackClickedTargetAudience();
    localActions.openManagePersonasPopup();
    uiActions.increaseLocalPopupVisibleCount();
  }, [localActions, trackClickedTargetAudience, uiActions]);

  const handleCloseManagePersonasPopup = useCallback(
    async (persona) => {
      if (persona) {
        handleSelectPersona(persona);
      }
      localActions.closeManagePersonasPopup();
      uiActions.decreaseLocalPopupVisibleCount();
    },
    [handleSelectPersona, localActions, uiActions]
  );

  const handlePersonaUpdated = useCallback(
    async (updatedPersona) => {
      if (updatedPersona?.id === localActions?.getSelectedPersona()?.id) {
        await localActions?.selectProjectPersona(updatedPersona);
      }
      brandVoicesActions.storeBrandVoiceItemInCache({
        itemsField: BRAND_VOICE_ASSET_FIELD.targetAudiences,
        item: updatedPersona,
        brandVoiceId: updatedPersona.brandVoice.id,
      });
    },
    [localActions, brandVoicesActions]
  );

  const handleEditPersona = useCallback(
    async (customerPersona) => {
      await openCustomerPersonaPopup({
        customerPersona,
        onUpdatedCallback: handlePersonaUpdated,
        brandVoice,
        onSave,
        isEditor: true,
      });
    },
    [openCustomerPersonaPopup, handlePersonaUpdated, brandVoice, onSave]
  );

  const handleDeletePersona = useCallback(
    async (persona) => {
      const deleted = await confirmDeletePersona(persona);
      if (!deleted) {
        return;
      }

      // In case persona was deleted, and it was selected previously - clear persona selection
      const selectedPersona = localActions.getSelectedPersona();
      if (selectedPersona?.id === persona.id) {
        await localActions.selectProjectPersona(null);
      }
      brandVoicesActions.removeBrandVoiceItemFromCache({
        itemsField: BRAND_VOICE_ASSET_FIELD.targetAudiences,
        item: persona,
      });
    },
    [confirmDeletePersona, localActions, brandVoicesActions]
  );

  const handlePersonaSeen = useCallback(
    (personaId) => {
      customerPersonaActions.setCustomerPersonaSeen(personaId);
    },
    [customerPersonaActions]
  );

  const handleSavePersona = useCallback(
    async (brandVoice, persona) => {
      const updatedPersona = await addCustomerPersonaToMyAudiences(
        brandVoice,
        persona,
        onSave,
        language
      );

      if (!updatedPersona) {
        return;
      }
      await handleSelectPersona(updatedPersona);

      brandVoicesActions.storeBrandVoiceItemInCache({
        itemsField: BRAND_VOICE_ASSET_FIELD.targetAudiences,
        item: updatedPersona,
        brandVoiceId: updatedPersona.brandVoice.id,
      });
    },
    [addCustomerPersonaToMyAudiences, brandVoicesActions, handleSelectPersona, onSave, language]
  );

  return {
    handleOpenManagePersonasPopup,
    handleCloseManagePersonasPopup,
    handleSelectPersona,
    handleEditPersona,
    handleDeletePersona,
    handlePersonaSeen,
    handlePersonaUpdated,
    handleSavePersona,
  };
};
